import ReactDOM from 'react-dom'
import React, { useRef, useState, ReactElement, cloneElement, Ref, PointerEvent, CSSProperties } from 'react'

import environment from 'environment'

import useKeylistener from 'hooks/useKeylistener'
import { useApplyBodyClass } from 'hooks/useScrollEffects'

import { DropdownProps, Option } from 'components/elements/ArchidektDropdown/types'
import { CardType } from 'types/deck'
import { LocalCardData } from 'components/card/deckCards/deckCardWrapper'

import DrawerMenu from 'components/elements/DrawerMenu'
import Menu from 'components/elements/ArchidektDropdown/Menu'
import DrawerTopper from 'components/card/DrawerTopper'
import BasicCard from 'components/card/BasicCard'

import styles from './contextMenu.module.scss'

const MOBILE_LONG_PRESS_DELAY = 500
const MOVEMENT_SLOP = 2

type Props = {
  id?: string
  ref?: Ref<HTMLDivElement>
  disabled?: boolean
  className?: string
  style?: React.CSSProperties

  // Options
  checkSkipRightClick?: () => boolean // Used to disabled whether or not right click does anything under certain contexts - can be useful if you have other right click actions that would conflict. Differs from disable in that this check happens in real time mid-event
  skipPinningOnLeftClickAction?: boolean

  // Callbacks
  onMouseDown?: (e: React.MouseEvent) => void
  onMouseMove?: (e: React.MouseEvent) => void
  onMouseUp?: (e: React.MouseEvent) => void
  onFocus?: (e: React.FocusEvent) => void

  // Treat menu as dropdown options
  openOnLeftClick?: boolean
  pinToLeftFirst?: boolean
  pinToTopFirst?: boolean
  matchMenuWidthToParent?: boolean
  customMenu?: ReactElement

  // Drawer options
  openMenuAsDrawerOnMobile?: boolean
  openMenuAsDrawerOnTouch?: boolean
  forceOpenAsDrawer?: boolean
  maxInitialOpenHeight?: number

  // Injects card data into the menu
  card?: CardType
  localCardData?: LocalCardData
  showFullCardInTopper?: boolean
} & DropdownProps

export type CustomMenuProps = { onClose?: () => void }

const ContextMenu = (props: Props) => {
  const pointerDownEvent = useRef<null | PointerEvent>(null)
  const pointerDownTimer = useRef<null | NodeJS.Timeout>(null)

  const menuStyles = useRef<CSSProperties>({})

  const menuWrapperRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)

  useApplyBodyClass(dropdownOpen, 'context-menu-open')

  useKeylistener(
    e => {
      if (!dropdownOpen && !drawerOpen) return

      if (e.key !== 'Escape') return

      e.preventDefault() // prevents exiting fullscreen on macos (I think)

      handleCloseOverlay()
    },
    [dropdownOpen, drawerOpen],
  )

  const generateMenuPositionAndSize = (e?: PointerEvent): CSSProperties => {
    let menuStyles: CSSProperties = { opacity: 0, left: e?.clientX, top: e?.clientY }

    // If we don't have a pointer event here, pin the menu to the trigger
    if (!props.openOnLeftClick && !!e) return menuStyles
    if (props.openOnLeftClick && props.skipPinningOnLeftClickAction && !!e) return menuStyles

    const trigger = containerRef?.current?.getBoundingClientRect()

    if (!trigger) return menuStyles

    if (props.pinToLeftFirst) menuStyles = { left: trigger.left, top: trigger.bottom }
    else menuStyles = { right: document.documentElement.clientWidth - trigger.right, top: trigger.bottom }

    if (props.pinToTopFirst) {
      menuStyles.top = undefined
      menuStyles.bottom = document.documentElement.clientHeight - trigger.top
    }

    if (props.matchMenuWidthToParent) menuStyles.maxWidth = trigger.width

    // NOTE - the opacity is always 0 here.
    // This is because we need the menu to be rendered (but not visable) until we double check that it's not off screen.
    // The opacity will be reset to 1 by handleMakeDrodownVisable after the collision detection has run (after the menu is open)
    return { ...menuStyles, opacity: 0 }
  }

  const handleMakeDropdownVisable = () => {
    if (!menuWrapperRef.current) return

    const menuSizeAndPosition = menuWrapperRef.current.getBoundingClientRect()

    const isClippingX = menuSizeAndPosition.left + menuSizeAndPosition.width > document.documentElement.clientWidth
    const isClippingY = menuSizeAndPosition.top + menuSizeAndPosition.height > document.documentElement.clientHeight

    if (isClippingX)
      menuWrapperRef.current.style.left = `${document.documentElement.clientWidth - menuSizeAndPosition.width}px`

    if (isClippingY)
      menuWrapperRef.current.style.top = `${document.documentElement.clientHeight - menuSizeAndPosition.height}px`

    menuWrapperRef.current.style.opacity = '1'
  }

  const handleCloseOverlay = (e?: React.MouseEvent) => {
    e?.preventDefault()
    e?.stopPropagation()

    setDropdownOpen(false)
    setDrawerOpen(false)
  }

  const handleMenuItemClick = (optionOnClick?: () => void, noCloseOnClick?: boolean) => {
    if (optionOnClick) optionOnClick()
    if (noCloseOnClick) return

    handleCloseOverlay()
  }

  const handleOpenWithoutEvent = () => {
    menuStyles.current = generateMenuPositionAndSize()

    handleOpenEvent()

    // Focus the dropdown's wrapping component so hitting tab moves the user into the menu - there's probably a better way to do this
    // Only doing this on keyboard open since that's likely the only one that'll need it
    setTimeout(() => menuWrapperRef.current?.focus(), 40)
  }

  const handleOpenEvent = (e?: React.PointerEvent) => {
    const isMobile = window.innerWidth < 600
    const openDrawer =
      props.forceOpenAsDrawer ||
      (e?.pointerType === 'touch' && props.openMenuAsDrawerOnTouch) ||
      (e?.pointerType === 'touch' && props.openMenuAsDrawerOnMobile && isMobile)

    // Fixes timing issue when opening the menu/ drawer. Without it, both close immediately because their off-click handleClose is fired onClick
    // This can likley be fixed in a b etter way
    setTimeout(() => {
      if (openDrawer) return setDrawerOpen(true)

      setDropdownOpen(true)

      setTimeout(() => handleMakeDropdownVisable(), 100)
    }, 20)

    pointerDownEvent.current = null
    pointerDownTimer.current = null
  }

  const handlePointerDown = (e: React.PointerEvent) => {
    if (e.ctrlKey || e.metaKey) return // Accounting for mac as well as non mac here
    if (e.button === 1) return // Middle mouse is button 1, we'll probably wanna double check that on ipad & trackpad

    // @ts-ignore
    if (!e.currentTarget.contains(e.target)) return

    // We never reset this because we don't need to. It'll be reset the next time the menu is opened, and we'll get the updated values
    // If the menu isn't open, it doesn't matter what these are since it's not rendered
    menuStyles.current = generateMenuPositionAndSize(e)

    // e.preventDefault() // Really want this on, but it's breaking the drag and drop in the playtester for mouse only for some reason
    e.stopPropagation()

    pointerDownEvent.current = e

    // Simulates right click action (long press) on mobile browsers
    pointerDownTimer.current = setTimeout(() => {
      if (!pointerDownEvent.current) return

      if (props.checkSkipRightClick) {
        const allowRightClick = props.checkSkipRightClick()

        if (!allowRightClick) return
      }

      handleOpenEvent(e)
    }, MOBILE_LONG_PRESS_DELAY)
  }

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!pointerDownEvent.current) return

    // Resetting state
    if (pointerDownTimer.current) clearTimeout(pointerDownTimer.current)

    pointerDownEvent.current = null
    pointerDownTimer.current = null

    if (e.button === 0 && !props.openOnLeftClick) return
    if (e.button === 2 && props.openOnLeftClick) return

    if (e.button === 2 && props.checkSkipRightClick) {
      const allowRightClick = props.checkSkipRightClick()

      if (!allowRightClick) return
    }

    e.preventDefault()
    e.stopPropagation()

    handleOpenEvent(e)
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    // Allow a bit of slop in the movement before assuming we're dragging
    // This is mostly for touch users
    if (Math.abs(e.movementX) < MOVEMENT_SLOP && Math.abs(e.movementY) < MOVEMENT_SLOP) return

    pointerDownEvent.current = null
  }

  const dropdownProps = { ...props, children: undefined, ref: undefined } // Remove children from dropdown props

  const menuLocation: React.CSSProperties = { ...menuStyles.current }

  // Appending card name header here so we can append the drawer topper in the drawer
  const dropdownOptions: Option[] = [
    { type: 'header', label: props.card?.name, hidden: !props.card },
    { type: 'spacer', hidden: !props.card },
    ...(dropdownProps.options || []),
  ]

  return (
    <div
      id={props.id}
      tabIndex={-1}
      ref={element => {
        // @ts-ignore
        if (containerRef) containerRef.current = element

        // @ts-ignore
        if (props.ref) props.ref.current = element
      }}
      className={`${styles.wrapper} ${props.className || ''}`}
      style={props.style}
      onClick={e => {
        e.stopPropagation()

        if (e.clientX === 0 && e.clientY === 0) handleOpenWithoutEvent()
      }}
      onContextMenu={e => {
        if (e.ctrlKey || e.metaKey) return

        e.preventDefault()
        e.stopPropagation()
      }}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerMove={handlePointerMove}
      // Only for parent use if they wanted to gain access to the div for one reason or another
      // Currently this is only used for multi-selection in the playtester playarea
      onMouseDown={props.onMouseDown}
      onMouseUp={props.onMouseUp}
      onMouseMove={props.onMouseMove}
      onFocus={props.onFocus}>
      {props.children}

      <DrawerMenu
        open={drawerOpen}
        onClose={handleCloseOverlay}
        maxInitialOpenHeight={props.maxInitialOpenHeight}
        options={props.options}
        customOptionsTop={
          props.customOptionsTop ??
          (props.card ? (
            props.showFullCardInTopper ? (
              <div className={styles.fullCardDrawerTopper}>
                <BasicCard card={props.card} />
              </div>
            ) : (
              <DrawerTopper
                card={props.card}
                localCardData={props.localCardData}
                includeCardText
                flipped={props.localCardData?.flipped || false}
              />
            )
          ) : undefined)
        }
      />

      {dropdownOpen &&
        ReactDOM.createPortal(
          <div id="context-menu-overlay" ref={overlayRef} className={styles.overlay} onClick={handleCloseOverlay}>
            <div
              ref={menuWrapperRef}
              style={menuLocation}
              className={`${styles.menu} ${dropdownProps.menuClassName || ''}`}
              tabIndex={-1}>
              {/* Used for the desktop more menu. If you wanted to build something very bespoke */}
              {props.customMenu && cloneElement(props.customMenu, { onClose: handleCloseOverlay })}

              {!props.customMenu && (
                <Menu
                  {...dropdownProps}
                  menuClassName={styles.menuInterior}
                  options={dropdownOptions}
                  unFocusClick={(_e, optionOnClick, noCloseOnClick) =>
                    handleMenuItemClick(optionOnClick, noCloseOnClick)
                  }
                />
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}

export default ContextMenu
